'use client'

import { useEffect, useState } from 'react'
import Logo from '@/components/ui/Logo'
import { useTheme } from '@/components/ThemeProvider'

/**
 * Cross-fade between the default 42A SVG and a partner logo image, with
 * a small scale + translate so the swap feels like one logo gracefully
 * ceding the slot to the other rather than a hard flip.
 *
 * Used by Sidebar, OnboardingLayout, and AuthLayout — all three pass a
 * resolved `partnerLogoUrl` (string when branded, null/undefined when not)
 * and the slot dimensions.
 *
 * Implementation notes:
 *   • Both layers (42A SVG + partner <img>) stay mounted at all times, so
 *     opacity + transform transitions have both endpoints in the DOM.
 *   • The partner layer keeps its *last-known* URL while fading out so
 *     it stays painted through the transition (avoids the visible "blank"
 *     that would happen if we cleared the src the moment branding goes
 *     away).
 *   • Easing is a custom ease-out (`cubic-bezier(0.22, 1, 0.36, 1)` —
 *     same family used by Apple's UI transitions) for a soft, premium
 *     deceleration rather than the linear-y feel of Tailwind's default.
 *   • `prefers-reduced-motion` users get an instant swap (no transition).
 */
interface Props {
  partnerLogoUrl: string | null | undefined
  /**
   * Dark-theme variant of the partner logo. When the active theme is dark and
   * this is set, it's shown instead of `partnerLogoUrl`. Falls back to the
   * light logo in dark mode when omitted (and vice versa).
   */
  partnerLogoDarkUrl?: string | null | undefined
  width: number
  height: number
  /**
   * Extra classes for the partner `<img>`. Defaults to `object-left` so
   * the visual content hugs the left edge of a wider slot — matching how
   * the 42A SVG is positioned. Pass a different `object-*` class to
   * override (e.g. `object-center` for narrow square slots).
   */
  imgClassName?: string
}

const DURATION_MS = 650
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)'
const TRANSITION = `opacity ${DURATION_MS}ms ${EASING}, transform ${DURATION_MS}ms ${EASING}, filter ${DURATION_MS}ms ${EASING}`

// 42A SVG viewBox aspect — used to compute natural-aspect dimensions for
// the <Logo /> so the SVG ends up exactly logo-sized inside the slot.
// Without this, the SVG's default preserveAspectRatio="xMidYMid meet"
// centers the logo content inside any excess slot width and the result
// looks adrift.
const LOGO_ASPECT = 377 / 140

/**
 * Compute the largest <Logo /> dimensions that fit inside `width × height`
 * while keeping the logo's natural aspect ratio. The wrapper's
 * `flex items-center` (default justify-start) then aligns the SVG flush
 * left within the slot.
 */
function fitLogoToSlot(width: number, height: number): { w: number; h: number } {
  const slotAspect = width / height
  if (slotAspect >= LOGO_ASPECT) {
    // Slot is wider than logo aspect → height-constrained.
    return { w: Math.round(height * LOGO_ASPECT), h: height }
  }
  // Slot is narrower → width-constrained.
  return { w: width, h: Math.round(width / LOGO_ASPECT) }
}

export default function BrandLogoSwap({
  partnerLogoUrl,
  partnerLogoDarkUrl,
  width,
  height,
  imgClassName = 'object-left',
}: Props) {
  const { resolvedTheme } = useTheme()
  // Pick the variant matching the active theme, falling back to the other so a
  // partner that only uploaded one logo still shows it in both themes.
  const effectiveLogoUrl =
    resolvedTheme === 'dark'
      ? (partnerLogoDarkUrl ?? partnerLogoUrl)
      : (partnerLogoUrl ?? partnerLogoDarkUrl)
  const showPartner = !!effectiveLogoUrl
  const logoDims = fitLogoToSlot(width, height)

  // Sticky URL: hold onto the most recent non-null URL so the partner
  // layer keeps rendering its image through the fade-out, and so a
  // partner→partner switch can briefly hold the previous logo while the
  // new one's <img> warms up.
  const [stickyUrl, setStickyUrl] = useState<string | null>(
    effectiveLogoUrl ?? null,
  )
  useEffect(() => {
    if (effectiveLogoUrl) setStickyUrl(effectiveLogoUrl)
  }, [effectiveLogoUrl])

  // Respect prefers-reduced-motion — collapse the transition to instant
  // so users who've opted out of motion don't see the scale/blur/slide.
  const [reducedMotion, setReducedMotion] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReducedMotion(mq.matches)
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const transition = reducedMotion ? 'none' : TRANSITION

  return (
    <div
      className="relative"
      style={{
        width,
        height,
        // Stack child layers; `isolation` keeps any future filter/blur
        // confined to this box.
        isolation: 'isolate',
      }}
    >
      {/* Default 42A SVG layer — fades + drifts down/back as the partner
          takes the slot. aria-hidden when not the active brand. */}
      <div
        className="absolute inset-0 flex items-center will-change-[opacity,transform]"
        style={{
          opacity: showPartner ? 0 : 1,
          transform: showPartner
            ? 'scale(0.94) translateY(3px)'
            : 'scale(1) translateY(0)',
          transformOrigin: 'left center',
          transition,
        }}
        aria-hidden={showPartner}
      >
        <Logo width={logoDims.w} height={logoDims.h} />
      </div>

      {/* Partner image layer — fades + rises into place. Slightly blurred
          while inactive so the entrance gets a subtle "focus pull" feel
          without being loud. */}
      <div
        className="absolute inset-0 will-change-[opacity,transform,filter]"
        style={{
          opacity: showPartner ? 1 : 0,
          transform: showPartner
            ? 'scale(1) translateY(0)'
            : 'scale(0.94) translateY(-3px)',
          transformOrigin: 'left center',
          filter: showPartner ? 'blur(0px)' : 'blur(4px)',
          transition,
        }}
        aria-hidden={!showPartner}
      >
        {stickyUrl && (
          // alt="" — decorative chrome; the partner name surfaces elsewhere
          // in surrounding UI (org switcher, sign-up subheading).
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={stickyUrl}
            alt=""
            className={`w-full h-full object-contain ${imgClassName}`}
            // Smooth out partner→partner URL changes — fade the img itself
            // when its src swaps. CSS-only, no extra state.
            style={{
              transition: reducedMotion
                ? 'none'
                : `opacity ${DURATION_MS}ms ${EASING}`,
            }}
          />
        )}
      </div>
    </div>
  )
}
